// =============================================================================
// CONFIGURATION WORKFLOW DOSSIER
// Les 12 étapes affichées en haut de la fiche dossier
// =============================================================================

import { useState, useEffect } from "react";
import { 
  Settings, Plus, Trash2, Save, X, GripVertical, 
  ArrowRight, CheckCircle2, Circle, Edit
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";
import { enteteTenant } from "../../utils/enteteTenant";

const API = process.env.REACT_APP_BACKEND_URL;

// Étapes par défaut du workflow dossier
const ETAPES_DEFAULT = [
  { numero: 1, code: "prospect", nom: "Prospect", couleur: "bg-zinc-500/20 border border-[var(--oh-border)]", actif: true },
  { numero: 2, code: "rdv_planifie", nom: "RDV planifié", couleur: "bg-[var(--oh-accent-solid)]", actif: true },
  { numero: 3, code: "visite_effectuee", nom: "Visite effectuée", couleur: "bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)]", actif: true },
  { numero: 4, code: "devis_envoye", nom: "Devis envoyé", couleur: "bg-[var(--oh-accent-solid)]", actif: true },
  { numero: 5, code: "devis_signe", nom: "Devis signé", couleur: "bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)]", actif: true },
  { numero: 6, code: "dossier_complet", nom: "Dossier complet", couleur: "bg-cyan-500/15 border border-cyan-500/30", actif: true },
  { numero: 7, code: "chantier_planifie", nom: "Chantier planifié", couleur: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]", actif: true },
  { numero: 8, code: "travaux_en_cours", nom: "Travaux en cours", couleur: "bg-amber-500", actif: true },
  { numero: 9, code: "travaux_termines", nom: "Travaux terminés", couleur: "bg-orange-500", actif: true },
  { numero: 10, code: "dossier_depose", nom: "Dossier déposé", couleur: "bg-lime-500", actif: true },
  { numero: 11, code: "dossier_valide", nom: "Dossier validé", couleur: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]", actif: true },
  { numero: 12, code: "dossier_paye", nom: "Dossier payé", couleur: "bg-[var(--oh-info-bg)] border border-[var(--oh-info-border)]", actif: true },
];

const COULEURS = [
  { code: "bg-zinc-500/20 border border-[var(--oh-border)]", label: "Gris" },
  { code: "bg-[var(--oh-accent-solid)]", label: "Bleu" },
  { code: "bg-[var(--oh-accent-bg)] border border-[var(--oh-accent)]", label: "Indigo" },
  { code: "bg-[var(--oh-accent-solid)]", label: "Violet" },
  { code: "bg-cyan-500/15 border border-cyan-500/30", label: "Cyan" },
  { code: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]", label: "Teal" },
  { code: "bg-amber-500", label: "Ambre" },
  { code: "bg-orange-500", label: "Orange" },
  { code: "bg-[var(--oh-success-bg)] border border-[var(--oh-success-border)]", label: "Vert" },
  { code: "bg-[var(--oh-info-bg)] border border-[var(--oh-info-border)]", label: "Émeraude" },
  { code: "bg-red-500", label: "Rouge" },
  { code: "bg-pink-500", label: "Rose" },
];

export default function WorkflowDossierConfig({ tenantId }) {
  const [etapes, setEtapes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingEtape, setEditingEtape] = useState(null);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("session_token");
    return {
      ...enteteTenant(tenantId),
      "Content-Type": "application/json",
      ...(token && { "Authorization": `Bearer ${token}` })
    };
  };

  useEffect(() => {
    loadConfig();
  }, [tenantId]);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/metier/config/workflow-dossier`, {
        headers: getAuthHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setEtapes(data.etapes?.length > 0 ? data.etapes : ETAPES_DEFAULT);
      } else {
        setEtapes(ETAPES_DEFAULT);
      }
    } catch (err) {
      console.error("Erreur chargement config:", err);
      setEtapes(ETAPES_DEFAULT);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/metier/config/workflow-dossier`, {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify({ etapes })
      });
      if (res.ok) {
        toast.success("Configuration enregistrée");
        setEditMode(false);
      } else {
        toast.error("Erreur lors de l'enregistrement");
      }
    } catch (err) {
      toast.error("Erreur lors de l'enregistrement");
    } finally {
      setSaving(false);
    }
  };

  const handleAddEtape = () => {
    setEditingEtape({
      numero: etapes.length + 1,
      code: "",
      nom: "",
      couleur: "bg-zinc-500/20 border border-[var(--oh-border)]",
      actif: true,
      isNew: true
    });
    setShowDialog(true);
  };

  const handleEditEtape = (etape) => {
    setEditingEtape({ ...etape });
    setShowDialog(true);
  };

  const handleSaveEtape = () => {
    if (!editingEtape.nom || !editingEtape.code) {
      toast.error("Nom et code obligatoires");
      return;
    }

    if (editingEtape.isNew) {
      setEtapes([...etapes, { ...editingEtape, isNew: undefined }]);
    } else {
      setEtapes(etapes.map(e => 
        e.numero === editingEtape.numero ? { ...editingEtape } : e
      ));
    }
    setShowDialog(false);
    setEditingEtape(null);
  };

  const handleDeleteEtape = (numero) => {
    if (window.confirm("Supprimer cette étape ?")) {
      const newEtapes = etapes
        .filter(e => e.numero !== numero)
        .map((e, idx) => ({ ...e, numero: idx + 1 }));
      setEtapes(newEtapes);
    }
  };

  const moveEtape = (index, direction) => {
    const newEtapes = [...etapes];
    const targetIndex = direction === "up" ? index - 1 : index + 1;
    if (targetIndex < 0 || targetIndex >= newEtapes.length) return;
    
    [newEtapes[index], newEtapes[targetIndex]] = [newEtapes[targetIndex], newEtapes[index]];
    newEtapes.forEach((e, idx) => e.numero = idx + 1);
    setEtapes(newEtapes);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[var(--oh-accent)]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-medium text-[var(--oh-text)] flex items-center gap-2">
            <Settings className="w-6 h-6 text-[var(--oh-accent-text)]" />
            Configuration Workflow Dossier
          </h2>
          <p className="text-[var(--oh-text-muted)] text-sm mt-1">
            Les étapes affichées en haut de chaque fiche dossier
          </p>
        </div>
        <div className="flex gap-2">
          {editMode ? (
            <>
              <Button variant="outline" onClick={() => { setEditMode(false); loadConfig(); }}>
                <X className="w-4 h-4 mr-2" />
                Annuler
              </Button>
              <Button onClick={saveConfig} disabled={saving} className="bg-green-600 hover:bg-green-700">
                <Save className="w-4 h-4 mr-2" />
                {saving ? "Enregistrement..." : "Enregistrer"}
              </Button>
            </>
          ) : (
            <Button onClick={() => setEditMode(true)} className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]">
              <Edit className="w-4 h-4 mr-2" />
              Modifier
            </Button>
          )}
        </div>
      </div>

      {/* Prévisualisation du workflow */}
      <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
        <CardHeader>
          <CardTitle className="text-[var(--oh-text)] text-sm">Prévisualisation</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-1 overflow-x-auto pb-2">
            {etapes.filter(e => e.actif).map((etape, idx) => (
              <div key={etape.numero} className="flex items-center">
                <div className={`px-3 py-1.5 rounded-full text-[var(--oh-text)] text-xs font-medium whitespace-nowrap ${etape.couleur}`}>
                  {etape.numero}. {etape.nom}
                </div>
                {idx < etapes.filter(e => e.actif).length - 1 && (
                  <ArrowRight className="w-4 h-4 text-[var(--oh-text-secondary)] mx-1 flex-shrink-0" />
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Liste des étapes */}
      <Card className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
        <CardHeader className="flex flex-col gap-2 md:flex-row md:gap-0 items-center justify-between">
          <CardTitle className="text-[var(--oh-text)]">Étapes du workflow</CardTitle>
          {editMode && (
            <Button size="sm" onClick={handleAddEtape} className="bg-green-600 hover:bg-green-700">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {etapes.map((etape, idx) => (
              <div 
                key={etape.numero}
                className={`flex items-center gap-4 p-3 rounded-lg ${
                  etape.actif ? "bg-[var(--oh-bg-surface)]" : "bg-[var(--oh-bg-surface)] opacity-50"
                }`}
              >
                {editMode && (
                  <div className="flex flex-col gap-1">
                    <button 
                      onClick={() => moveEtape(idx, "up")}
                      disabled={idx === 0}
                      className="text-[var(--oh-text-muted)] hover:text-[var(--oh-text)] disabled:opacity-30"
                    >
                      ▲
                    </button>
                    <button 
                      onClick={() => moveEtape(idx, "down")}
                      disabled={idx === etapes.length - 1}
                      className="text-[var(--oh-text-muted)] hover:text-[var(--oh-text)] disabled:opacity-30"
                    >
                      ▼
                    </button>
                  </div>
                )}
                
                <div className="flex items-center gap-3 flex-1">
                  <div className={`w-8 h-8 rounded-full ${etape.couleur} flex items-center justify-center text-[var(--oh-text)] font-medium text-sm`}>
                    {etape.numero}
                  </div>
                  <div>
                    <p className="text-[var(--oh-text)] font-medium">{etape.nom}</p>
                    <p className="text-[var(--oh-text-muted)] text-xs font-mono">{etape.code}</p>
                  </div>
                </div>

                <Badge className={etape.actif ? "bg-[var(--oh-success-bg)] text-[var(--oh-success-text)]" : "bg-[var(--oh-bg-elevated)]/20 text-[var(--oh-text-muted)]"}>
                  {etape.actif ? "Actif" : "Inactif"}
                </Badge>

                {editMode && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="ghost" onClick={() => handleEditEtape(etape)}>
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={() => handleDeleteEtape(etape.numero)}
                      className="text-[var(--oh-error-text)] hover:text-[var(--oh-error-text)]"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Dialog édition étape */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-[var(--oh-bg-elevated)] border-[var(--oh-border)]">
          <DialogHeader>
            <DialogTitle className="text-[var(--oh-text)]">
              {editingEtape?.isNew ? "Ajouter une étape" : "Modifier l'étape"}
            </DialogTitle>
          </DialogHeader>
          
          {editingEtape && (
            <div className="space-y-4">
              <div>
                <Label className="text-[var(--oh-text-muted)]">Numéro</Label>
                <Input
                  type="number"
                  value={editingEtape.numero}
                  onChange={(e) => setEditingEtape({ ...editingEtape, numero: parseInt(e.target.value) })}
                  className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                />
              </div>
              
              <div>
                <Label className="text-[var(--oh-text-muted)]">Nom</Label>
                <Input
                  value={editingEtape.nom}
                  onChange={(e) => setEditingEtape({ ...editingEtape, nom: e.target.value })}
                  placeholder="Ex: Devis signé"
                  className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]"
                />
              </div>

              <div>
                <Label className="text-[var(--oh-text-muted)]">Code technique</Label>
                <Input
                  value={editingEtape.code}
                  onChange={(e) => setEditingEtape({ ...editingEtape, code: e.target.value.toLowerCase().replace(/\s+/g, "_") })}
                  placeholder="Ex: devis_signe"
                  className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)] font-mono"
                />
              </div>

              <div>
                <Label className="text-[var(--oh-text-muted)]">Couleur</Label>
                <Select 
                  value={editingEtape.couleur} 
                  onValueChange={(v) => setEditingEtape({ ...editingEtape, couleur: v })}
                >
                  <SelectTrigger className="bg-[var(--oh-bg-surface)] border-[var(--oh-border)] text-[var(--oh-text)]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COULEURS.map(c => (
                      <SelectItem key={c.code} value={c.code}>
                        <div className="flex items-center gap-2">
                          <div className={`w-4 h-4 rounded-full ${c.code}`} />
                          {c.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={editingEtape.actif}
                  onChange={(e) => setEditingEtape({ ...editingEtape, actif: e.target.checked })}
                  className="rounded"
                />
                <Label className="text-[var(--oh-text-muted)]">Étape active</Label>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Annuler
            </Button>
            <Button onClick={handleSaveEtape} className="bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)]">
              Enregistrer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
